import { useCallback, useEffect, useMemo } from 'react';
import { throttle } from 'lodash';

const DefaultOptions = {
  /**
   * timeout in millis to call after scroll has happened
   */
  throttleMs: 200,

  /**
   * Offset from bottom of scroll ref in pixels. E.g. 300 will trigger onBottom 300px from the bottom of the ref
   */
  offsetPx: 200,
};

export default function useBottomScrollListener(ref, callback, options) {
  const { throttleMs, offsetPx } = useMemo(() => ({ ...DefaultOptions, options }), [options]);

  const throttledCallback = useMemo(
    () => throttle(callback || (() => {}), throttleMs),
    [callback, throttleMs],
  );

  const onScroll = useCallback(() => {
    if (!ref) {
      return;
    }

    const scrollNode = ref;
    const scrollContainerBottomPosition = Math.round(
      scrollNode.scrollTop + scrollNode.clientHeight,
    );
    const scrollPosition = Math.round(scrollNode.scrollHeight - offsetPx);

    if (scrollPosition <= scrollContainerBottomPosition) {
      throttledCallback();
    }
  }, [ref, throttledCallback, offsetPx]);

  useEffect(() => {
    if (callback) {
      ref?.addEventListener('scroll', onScroll);
    }

    return () => {
      ref?.removeEventListener('scroll', onScroll);
    };
  }, [ref, callback]);
}
